import {Button, Flex, Modal, Typography} from 'antd'
import {CheckCircleTwoTone, CloseCircleOutlined, InfoCircleOutlined, WarningOutlined} from '@ant-design/icons'

// Structural result shape shared by every *config Result message (providers, casinos, ...).
type ResultLike = {
  errors?: { [key: string]: string }
  warnings?: { [key: string]: string }
  infos?: { [key: string]: string }
}

type Props = {
  open: boolean
  title: string
  res?: ResultLike
  onClose: () => void
  // Continue button is rendered only when onContinue is provided.
  onContinue?: () => void
  continueText?: string
  continueDisabled?: boolean
}

function section(
  items: [string, string][],
  color: string,
  bg: string,
  icon: React.ReactNode,
  label: string,
) {
  if (items.length === 0) return null
  return (
    <div style={{marginBottom: 16}} key={label}>
      <Flex align="center" gap={6} style={{marginBottom: 8}}>
        {icon}
        <Typography.Text strong style={{color}}>{label}</Typography.Text>
        <span style={{
          color, background: bg, borderRadius: 10, padding: '0 8px',
          fontSize: 12, fontWeight: 600, lineHeight: '18px',
        }}>{items.length}</span>
      </Flex>
      <Flex vertical gap={6}>
        {items.map(([key, msg]) => (
          <div key={key} style={{
            padding: '8px 10px', borderRadius: 6,
            background: bg, borderLeft: `3px solid ${color}`,
          }}>
            <Typography.Text strong style={{fontSize: 14, color}}>{key}</Typography.Text>
            {msg && (
              <Typography.Text type="secondary" style={{display: 'block', marginTop: 2, fontSize: 12}}>
                {msg}
              </Typography.Text>
            )}
          </div>
        ))}
      </Flex>
    </div>
  )
}

export default function ResultModal(props: Props) {
  const {open, title, res, onClose, onContinue, continueText, continueDisabled} = props

  const errors = Object.entries(res?.errors ?? {})
  const warnings = Object.entries(res?.warnings ?? {})
  const infos = Object.entries(res?.infos ?? {})

  const clean = errors.length === 0 && warnings.length === 0 && infos.length === 0

  const footer = [
    <Button key="close" onClick={onClose}>Close</Button>,
    onContinue && (
      <Button key="continue" type="primary" disabled={continueDisabled} onClick={onContinue}>
        {continueText ?? 'Continue'}
      </Button>
    ),
  ].filter(Boolean)

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width="80vw"
      footer={footer}
    >
      {clean ? (
        <Flex vertical align="center" gap={10} style={{padding: '16px 0'}}>
          <CheckCircleTwoTone twoToneColor="#52c41a" style={{fontSize: 52}} />
          <Typography.Text type="secondary">No issues found for this branch.</Typography.Text>
        </Flex>
      ) : (
        <div style={{maxHeight: 440, overflowY: 'auto', marginTop: 12}}>
          {section(errors, '#cf1322', '#fff1f0', <CloseCircleOutlined style={{color: '#cf1322'}} />, 'Errors')}
          {section(warnings, '#d46b08', '#fffbe6', <WarningOutlined style={{color: '#d46b08'}} />, 'Warnings')}
          {section(infos, '#0958d9', '#e6f4ff', <InfoCircleOutlined style={{color: '#0958d9'}} />, 'Infos')}
        </div>
      )}
    </Modal>
  )
}